import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  AlertTriangle,
  Check,
  Copy,
  Link2,
  Loader2,
  Plug,
  Plus,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { formatDateTime } from '@/lib/format';
import {
  createMcpToken,
  getMcpTokens,
  revokeMcpToken,
  type CreatedMcpToken,
  type McpToken,
} from '@/services/mcp.service';

/**
 * Einstellungen → MCP-Zugriff.
 *
 * Zeigt die persönliche MCP-Endpoint-URL (kommt vom Server, wird NIE
 * clientseitig zusammengebaut) und verwaltet die Zugriffstokens des Benutzers.
 * Der volle Token existiert nur genau einmal — direkt nach der Erstellung im
 * State dieser Komponente. Nach dem Schließen ist er unwiederbringlich weg.
 */

/** Platzhalter in den Code-Snippets — bewusst sprachneutral, kein i18n. */
const TOKEN_PLACEHOLDER = 'YOUR_TOKEN';

type CopyTarget = 'url' | 'new-token' | 'snippet-claude-code' | 'snippet-other';

export default function McpSection() {
  const { t } = useTranslation('settings');
  const queryClient = useQueryClient();

  const [tokenName, setTokenName] = useState('');
  const [newToken, setNewToken] = useState<CreatedMcpToken | null>(null);
  const [copied, setCopied] = useState<CopyTarget | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['mcpTokens'],
    queryFn: getMcpTokens,
  });

  const tokens: McpToken[] = data?.data?.tokens ?? [];
  const mcpUrl = data?.data?.mcpUrl ?? '';

  const createMutation = useMutation({
    mutationFn: (name: string) => createMcpToken(name),
    onSuccess: (result) => {
      setNewToken(result.data);
      setTokenName('');
      queryClient.invalidateQueries({ queryKey: ['mcpTokens'] });
      toast.success(t('mcp.tokens.created', 'Token erstellt'));
    },
    onError: () => {
      toast.error(t('mcp.tokens.createFailed', 'Token konnte nicht erstellt werden'));
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => revokeMcpToken(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['mcpTokens'] });
      toast.success(t('mcp.tokens.revoked', 'Token widerrufen'));
    },
    onError: () => {
      toast.error(t('mcp.tokens.revokeFailed', 'Token konnte nicht widerrufen werden'));
    },
  });

  const copy = async (text: string, target: CopyTarget) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(target);
      window.setTimeout(() => setCopied((current) => (current === target ? null : current)), 2000);
      toast.success(t('mcp.copySuccess', 'In die Zwischenablage kopiert'));
    } catch {
      toast.error(t('mcp.copyFailed', 'Kopieren fehlgeschlagen'));
    }
  };

  const handleCreate = () => {
    const name = tokenName.trim();
    if (!name || createMutation.isPending) return;
    createMutation.mutate(name);
  };

  const tokenStatus = (token: McpToken) => {
    if (token.revoked_at) {
      return { label: t('mcp.tokens.status.revoked', 'Widerrufen'), tone: 'revoked' as const };
    }
    if (token.expires_at && new Date(token.expires_at).getTime() < Date.now()) {
      return { label: t('mcp.tokens.status.expired', 'Abgelaufen'), tone: 'expired' as const };
    }
    return { label: t('mcp.tokens.status.active', 'Aktiv'), tone: 'active' as const };
  };

  const urlForSnippet = mcpUrl || 'https://…/mcp';
  const tokenForSnippet = newToken?.token ?? TOKEN_PLACEHOLDER;
  const claudeCodeSnippet = `claude mcp add --transport http vyndesk ${urlForSnippet} --header "Authorization: Bearer ${tokenForSnippet}"`;
  const otherSnippet = `Authorization: Bearer ${tokenForSnippet}`;

  return (
    <div className="space-y-6">
      {/* Einführung + Server-URL */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="border-b border-slate-100 bg-white">
          <CardTitle className="flex items-center text-lg">
            <div className="mr-3 flex h-10 w-10 items-center justify-center rounded-lg bg-[#0f172a]">
              <Plug className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-bold text-slate-900">{t('mcp.heading', 'MCP-Zugriff')}</div>
              <CardDescription className="mt-1 text-sm">
                {t(
                  'mcp.intro',
                  'MCP (Model Context Protocol) verbindet KI-Clients wie Claude oder ChatGPT direkt mit Ihrem VynDesk-Arbeitsbereich.',
                )}
              </CardDescription>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <p className="max-w-3xl text-sm leading-6 text-slate-500">
            {t(
              'mcp.introDetail',
              'Ein verbundener Client kann Ihre Tickets lesen und bearbeiten — mit genau den Rechten, die Ihr Benutzerkonto hat. Tokens sind persönlich und sollten niemals geteilt werden.',
            )}
          </p>

          <div className="space-y-2">
            <Label className="text-xs font-medium text-slate-700">
              {t('mcp.serverUrl.label', 'MCP-Server-URL')}
            </Label>
            <div className="flex items-center gap-2">
              <code className="flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5 font-mono text-xs leading-relaxed text-slate-800">
                <Link2 className="h-3.5 w-3.5 shrink-0 text-slate-400" />
                <span className="truncate">
                  {isLoading ? t('mcp.loading', 'Wird geladen …') : mcpUrl || '—'}
                </span>
              </code>
              <Button
                type="button"
                variant="outline"
                size="icon"
                disabled={!mcpUrl}
                onClick={() => copy(mcpUrl, 'url')}
                aria-label={t('mcp.copy', 'Kopieren')}
                className="h-9 w-9 shrink-0 hover:bg-slate-100"
              >
                {copied === 'url' ? (
                  <Check className="h-3.5 w-3.5 text-green-600" />
                ) : (
                  <Copy className="h-3.5 w-3.5 text-slate-600" />
                )}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              {t('mcp.serverUrl.hint', 'Diese Adresse tragen Sie in Ihrem MCP-Client ein.')}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Tokens */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="border-b border-slate-100 bg-white">
          <CardTitle className="text-base font-semibold text-slate-900">
            {t('mcp.tokens.title', 'Zugriffstokens')}
          </CardTitle>
          <CardDescription className="text-sm text-slate-500">
            {t(
              'mcp.tokens.description',
              'Erstellen Sie pro Client ein eigenes Token — so können Sie einzelne Zugänge gezielt widerrufen.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5 pt-6">
          {/* Neu erstellter Token — nur einmal sichtbar */}
          {newToken && (
            <div className="space-y-3 rounded-lg border border-amber-200 bg-amber-50 p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-amber-900">
                    {t('mcp.tokens.newTitle', 'Token nur jetzt sichtbar')}
                  </p>
                  <p className="mt-1 text-xs leading-5 text-amber-800">
                    {t(
                      'mcp.tokens.newWarning',
                      'Kopieren Sie diesen Token jetzt und bewahren Sie ihn sicher auf. Er wird nie wieder angezeigt.',
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <code className="min-w-0 flex-1 break-all rounded-lg bg-slate-900 px-3 py-2.5 font-mono text-xs leading-relaxed text-green-400">
                  {newToken.token}
                </code>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => copy(newToken.token, 'new-token')}
                  aria-label={t('mcp.copy', 'Kopieren')}
                  className="h-9 w-9 shrink-0 bg-white hover:bg-slate-100"
                >
                  {copied === 'new-token' ? (
                    <Check className="h-3.5 w-3.5 text-green-600" />
                  ) : (
                    <Copy className="h-3.5 w-3.5 text-slate-600" />
                  )}
                </Button>
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => setNewToken(null)}
                className="h-8 border-amber-300 bg-white text-xs text-amber-900 hover:bg-amber-100"
              >
                {t('mcp.tokens.dismiss', 'Ich habe den Token gespeichert')}
              </Button>
            </div>
          )}

          {/* Erstellen */}
          <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor="mcp-token-name" className="text-xs font-medium text-slate-700">
                {t('mcp.tokens.nameLabel', 'Name des Tokens')}
              </Label>
              <Input
                id="mcp-token-name"
                value={tokenName}
                onChange={(event) => setTokenName(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleCreate();
                  }
                }}
                placeholder={t('mcp.tokens.namePlaceholder', 'z.B. Claude auf dem Laptop')}
                className="h-9 border-slate-200 focus-visible:ring-slate-200"
              />
            </div>
            <Button
              type="button"
              onClick={handleCreate}
              disabled={!tokenName.trim() || createMutation.isPending}
              className="h-9 gap-2 bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50"
            >
              {createMutation.isPending ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  {t('mcp.tokens.creating', 'Wird erstellt …')}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {t('mcp.tokens.create', 'Token erstellen')}
                </>
              )}
            </Button>
          </div>

          {/* Liste */}
          <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            {isLoading ? (
              <div className="flex items-center justify-center gap-2 py-10 text-sm text-slate-500">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('mcp.loading', 'Wird geladen …')}
              </div>
            ) : isError ? (
              <div className="py-10 text-center text-sm text-slate-500">
                {t('mcp.tokens.loadFailed', 'Tokens konnten nicht geladen werden')}
              </div>
            ) : tokens.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-500">
                {t('mcp.tokens.empty', 'Noch kein Token vorhanden')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('mcp.tokens.col.name', 'Name')}</TableHead>
                    <TableHead>{t('mcp.tokens.col.prefix', 'Token')}</TableHead>
                    <TableHead>{t('mcp.tokens.col.created', 'Erstellt')}</TableHead>
                    <TableHead>{t('mcp.tokens.col.lastUsed', 'Zuletzt genutzt')}</TableHead>
                    <TableHead>{t('mcp.tokens.col.status', 'Status')}</TableHead>
                    <TableHead className="text-right">
                      {t('mcp.tokens.col.actions', 'Aktionen')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tokens.map((token) => {
                    const status = tokenStatus(token);
                    return (
                      <TableRow key={token.id}>
                        <TableCell className="font-medium text-slate-900">{token.name}</TableCell>
                        <TableCell>
                          <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-700">
                            {token.token_prefix}…
                          </code>
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {formatDateTime(token.created_at)}
                        </TableCell>
                        <TableCell className="text-xs text-slate-500">
                          {token.last_used_at
                            ? formatDateTime(token.last_used_at)
                            : t('mcp.tokens.neverUsed', 'Nie')}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={status.tone === 'active' ? 'default' : 'secondary'}
                            className={
                              status.tone === 'active'
                                ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-100'
                            }
                          >
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          {status.tone === 'revoked' ? null : (
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  disabled={revokeMutation.isPending}
                                  className="h-8 gap-1.5 text-xs text-red-600 hover:bg-red-50 hover:text-red-700"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  {t('mcp.tokens.revoke', 'Widerrufen')}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>
                                    {t('mcp.tokens.revokeTitle', 'Token widerrufen?')}
                                  </AlertDialogTitle>
                                  <AlertDialogDescription>
                                    {t('mcp.tokens.revokeDescription', {
                                      defaultValue:
                                        'Clients, die "{{name}}" verwenden, verlieren sofort den Zugriff. Das lässt sich nicht rückgängig machen.',
                                      name: token.name,
                                    })}
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>
                                    {t('mcp.tokens.revokeCancel', 'Abbrechen')}
                                  </AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => revokeMutation.mutate(token.id)}
                                    className="bg-red-600 text-white hover:bg-red-700"
                                  >
                                    {t('mcp.tokens.revokeConfirm', 'Widerrufen')}
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Einrichtung */}
      <Card className="border border-white/70 bg-white/70 backdrop-blur-xl ring-1 ring-black/5">
        <CardHeader className="border-b border-slate-100 bg-white">
          <CardTitle className="text-base font-semibold text-slate-900">
            {t('mcp.setup.title', 'Einrichtung')}
          </CardTitle>
          <CardDescription className="text-sm text-slate-500">
            {t(
              'mcp.setup.description',
              'So verbinden Sie Ihren Client. Ersetzen Sie den Platzhalter durch Ihren Token.',
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-6">
          <Tabs defaultValue="claude-code" className="w-full">
            <TabsList className="mb-4">
              <TabsTrigger value="claude-code">
                {t('mcp.setup.tab.claudeCode', 'Claude Code')}
              </TabsTrigger>
              <TabsTrigger value="connectors">
                {t('mcp.setup.tab.connectors', 'Claude.ai / ChatGPT')}
              </TabsTrigger>
              <TabsTrigger value="other">{t('mcp.setup.tab.other', 'Andere Clients')}</TabsTrigger>
            </TabsList>

            <TabsContent value="claude-code" className="space-y-3">
              <p className="text-sm leading-6 text-slate-500">
                {t(
                  'mcp.setup.claudeCode.description',
                  'Führen Sie diesen Befehl im Terminal aus — Claude Code merkt sich den Server dauerhaft.',
                )}
              </p>
              <CodeSnippet
                value={claudeCodeSnippet}
                copied={copied === 'snippet-claude-code'}
                onCopy={() => copy(claudeCodeSnippet, 'snippet-claude-code')}
                copyLabel={t('mcp.copy', 'Kopieren')}
              />
              <p className="text-xs text-slate-500">
                {t(
                  'mcp.setup.claudeCode.note',
                  'Prüfen Sie die Verbindung anschließend mit "claude mcp list".',
                )}
              </p>
            </TabsContent>

            <TabsContent value="connectors" className="space-y-3">
              <p className="text-sm leading-6 text-slate-500">
                {t(
                  'mcp.setup.connectors.description',
                  'Fügen Sie in den Einstellungen von Claude.ai bzw. ChatGPT einen eigenen Connector hinzu und tragen Sie dort die MCP-Server-URL ein.',
                )}
              </p>
              <CodeSnippet
                value={urlForSnippet}
                copied={copied === 'url'}
                onCopy={() => copy(mcpUrl, 'url')}
                copyLabel={t('mcp.copy', 'Kopieren')}
              />
              <p className="text-xs leading-5 text-slate-500">
                {t(
                  'mcp.setup.connectors.oauth',
                  'Beim Verbinden werden Sie zur Anmeldung weitergeleitet: Der OAuth-Ablauf nutzt Ihren normalen VynDesk-Login. Ein Token ist dafür nicht nötig.',
                )}
              </p>
            </TabsContent>

            <TabsContent value="other" className="space-y-3">
              <p className="text-sm leading-6 text-slate-500">
                {t(
                  'mcp.setup.other.description',
                  'Jeder Client mit Streamable-HTTP-Transport funktioniert. Verwenden Sie die MCP-Server-URL und senden Sie den Token als Bearer-Header mit.',
                )}
              </p>
              <CodeSnippet
                value={otherSnippet}
                copied={copied === 'snippet-other'}
                onCopy={() => copy(otherSnippet, 'snippet-other')}
                copyLabel={t('mcp.copy', 'Kopieren')}
              />
              <ul className="space-y-1 text-xs text-slate-500">
                <li>{t('mcp.setup.other.transport', 'Transport: Streamable HTTP')}</li>
                <li>{t('mcp.setup.other.auth', 'Authentifizierung: Bearer-Token')}</li>
              </ul>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

interface CodeSnippetProps {
  value: string;
  copied: boolean;
  onCopy: () => void;
  copyLabel: string;
}

/** Kopierbarer Code-Block — bewusst mit break-all, damit lange URLs umbrechen. */
function CodeSnippet({ value, copied, onCopy, copyLabel }: CodeSnippetProps) {
  return (
    <div className="flex items-start gap-2">
      <code className="min-w-0 flex-1 whitespace-pre-wrap break-all rounded-lg bg-slate-900 px-3 py-2.5 font-mono text-xs leading-relaxed text-slate-100">
        {value}
      </code>
      <Button
        type="button"
        variant="outline"
        size="icon"
        onClick={onCopy}
        aria-label={copyLabel}
        className="h-9 w-9 shrink-0 hover:bg-slate-100"
      >
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600" />
        ) : (
          <Copy className="h-3.5 w-3.5 text-slate-600" />
        )}
      </Button>
    </div>
  );
}
